import { EmptyState } from '../../../components';
import { SectionCard } from '../SectionCard';
import { useMfaStatus } from '../useAccount';

/**
 * `GET /auth/mfa/status`, group `mfa`. Read-only, and deliberately only that.
 *
 * The route works — it is mounted and returns a real `GetMFAStatusResponseDTO`
 * — but against the running API `availableMethods` comes back `[]` for every
 * account tried. `auth.config.ts` sets no `mfa:` block (no TOTP, no SMS, no
 * passkey provider configured), and nauth's own route manifest filters an
 * MFA-enrolment route out of a mount entirely when its required service is
 * absent — `mfa/setup-data`, `mfa/verify-setup` and
 * `mfa/backup-codes/generate` exist in the SDK but there is nothing behind
 * them to enrol into here. Building an "Enable MFA" button against that would
 * be UI for a capability this deployment does not actually offer, so this
 * section only surfaces the status nauth already tracks (enrolled or not,
 * which methods, whether backup codes exist) and says plainly why there is
 * nothing to turn on. Wiring up an actual method is an API-side change (adding
 * an `mfa:` block to `auth.config.ts`), outside this round's file ownership.
 */
export function MfaSection() {
  const query = useMfaStatus();

  return (
    <SectionCard title="Multi-factor authentication">
      {query.isPending ? (
        <p className="fs-12 text-muted">Loading…</p>
      ) : query.error !== null ? (
        <EmptyState title="Could not load MFA status" description={query.error.message} />
      ) : (
        <div className="flex flex-col gap-2 fs-13">
          <p className="text-ink-soft">
            {query.data.enabled
              ? `Enabled — ${query.data.configuredMethods.join(', ')}.`
              : 'Not enabled on your account.'}
          </p>
          {query.data.availableMethods.length === 0 && (
            <p className="fs-12 text-muted">
              No verification methods are configured for this workspace yet, so there is nothing to enrol into
              here.
            </p>
          )}
        </div>
      )}
    </SectionCard>
  );
}
